import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { When } from 'react-if';
import { connect } from 'react-redux';
import {
  isWomanSeat,
  isPetFriendlySeat,
  evictAdaCompanions,
  isSeatOccupied,
} from 'utils/purchase/seats';
import { Box, Checkbox, Spacing, Text, Toggle, Badge } from '@reservamos/elements';
import Loading from 'components/Loading';
import CheckoutNavigation from 'components/purchase/CheckoutNavigation';
import SeatsLegend from 'components/SeatsLegend';
import ConnectedBusDiagram from 'components/purchase/BusDiagram';
import { withTranslation } from 'react-i18next';
import MobileBottomContainer from 'ui/atoms/MobileBottomContainer';
import isEqual from 'lodash/isEqual';
import { getSeatsCount, isSeatsOnResultActivated } from 'utils/seats';
import calculateAdjacentCardVisibility, {
  prepareAdjacentSeat,
  checkIfAdjacentPicked,
  checkIfAdjacentSelected,
  getSeatFromBusDiagram,
  findAdjacentSeatPair,
} from 'utils/adjacentCard';
import NewSeatsLegend from 'components/NewSeatsLegend';
import { trackViewSeats } from 'metrics/user-analytics/search';
import { continueWithSeatsTracker } from 'metrics/user-analytics/purchase';
import { trackSeatTapped } from 'metrics/user-analytics/seats';
import NewSeatsLayout from 'ui/templates/NewSeatsLayout';
import MobileDetailResult from 'ui/atoms/MobileDetailResult';
import { isDesktopSize } from 'utils/Reserbus';
import ModalWithTheme from 'components/ModalWithTheme';
import SeatsPreviewPricing from 'components/seats/SeatsPreviewPricing';
import BusDiagramNotFoundError from 'ui/molecules/BusDiagramNotFoundError';
import DotersSessionAlert from 'ui/atoms/DotersSessionAlert';
import PetBadge from 'images/badges/pet.svg';
import { CurrentLang } from 'utils/lang';
import AdjacentSeatOnboarding from 'ui/atoms/AdjacentSeatOnboarding';
import OverbookingPassengerSelector from 'ui/atoms/OverbookingPassengerSelector';
import PetsDialog from '../../ui/atoms/PetsDialog';
import InfoText from '../../ui/atoms/InfoText';
import HeaderDiagramBus from '../HeaderDiagramBus';
import MainButton from '../../ui/atoms/MainButton';
import NotAllowedSeats from './NotAllowedSeats';
import CounterSeat from '../../ui/atoms/CounterSeat';
import SelectedSeats from '../SelectedSeats';
import AdjacentSeatCard from '../../ui/atoms/AdjacentSeatCard';

import AdjacentSeatWarningDialog from '../../ui/atoms/AdjacentSeatWarningDialog';
import AdjacentSeatDisclaimerDialog from '../../ui/atoms/AdjacentSeatDisclaimerDialog';
import { getSeatZoneNumberForPassengerSeat } from '../../utils/seatPricing';
import {
  getResolvedSeatPriceZonesForSeatSelector,
  getSeatSelectorDepartureRoute,
} from '../../utils/seatSelectorRoute';
import { isBfoSupportedSeatSelectionFlow } from '../../utils/purchase/baseFareOverride';
import saveConfirmedSeatPreference from '../../utils/purchase/seatSelectionConfirmation';
import {
  cloneSelectedSeatReferences,
  isPrimarySeatReference,
} from '../../utils/purchase/seatReferenceTransport';

const propTypes = {
  diagramType: PropTypes.string,
  way: PropTypes.string.isRequired,
  layout: PropTypes.array,
  seats: PropTypes.object,
  passengers: PropTypes.array,
  selectedSeats: PropTypes.array.isRequired,
  lockedSeats: PropTypes.array.isRequired,
  canGoBack: PropTypes.bool,
  selectSeats: PropTypes.func.isRequired,
  sendNotification: PropTypes.func.isRequired,
  onBackAction: PropTypes.func,
  onFinishedSelection: PropTypes.func.isRequired,
  allowsSeatSelection: PropTypes.bool.isRequired,
  isExchange: PropTypes.bool,
  isOpenTicket: PropTypes.bool,
  t: PropTypes.func.isRequired,
  features: PropTypes.object.isRequired,
  busCategories: PropTypes.array,
  minPassengers: PropTypes.number,
  updateSeat: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  isPetFriendly: PropTypes.bool,
  tripPricing: PropTypes.object,
  tripId: PropTypes.string,
  firstFloorReclination: PropTypes.string,
  secondFloorReclination: PropTypes.string,
  resultsLayout: PropTypes.bool,
  onClose: PropTypes.func,
  hasSeats: PropTypes.bool,
  trip: PropTypes.object,
  clearError: PropTypes.func,
  fetchingDetailsError: PropTypes.bool,
  copies: PropTypes.object.isRequired,
  passengerTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    }),
  ),
  isMultiTrip: PropTypes.bool,
  firstTripIsActive: PropTypes.bool,
  secondTripIsActive: PropTypes.bool,
  routeLabel: PropTypes.string,
  headerLegend: PropTypes.string,
  roundTrip: PropTypes.bool,
  hasAdjacentSeatPassenger: PropTypes.bool,
  adjacentSeatPolicy: PropTypes.oneOf(['primary_and_adjacent', 'primary_only', 'disabled']),
  overbooking: PropTypes.bool,
  seatPriceZones: PropTypes.array,
  onQuickPurchaseFromSeats: PropTypes.func,
  frequentPassengersCount: PropTypes.number,
  showPricesByGroupConfig: PropTypes.object,
  embedded: PropTypes.bool,
  seatEditMode: PropTypes.oneOf(['quickCheckout']),
  requiredSeatsCount: PropTypes.number,
  bookingPassengerCount: PropTypes.number,
  seatPreferenceDiagramType: PropTypes.oneOf(['bus', 'van', 'unknown']),
};

const defaultProps = {
  isExchange: false,
  canGoBack: false,
  adjacentSeatPolicy: 'disabled',
  diagramType: 'bus',
  passengers: [],
  seats: {},
  minPassengers: 0,
  isMultiTrip: false,
  firstTripIsActive: false,
  secondTripIsActive: false,
  routeLabel: '',
  headerLegend: '',
  roundTrip: false,
  hasAdjacentSeatPassenger: false,
  overbooking: false,
  seatPriceZones: [],
  showPricesByGroupConfig: undefined,
  embedded: false,
  seatEditMode: undefined,
  requiredSeatsCount: 0,
  bookingPassengerCount: null,
  seatPreferenceDiagramType: 'unknown',
};

/**
 * Fare type label for analytics from the seat payload (API camelCase only).
 * Prefers seatFareReference; when null/undefined/empty uses seatFareDescription.
 * @param {object|undefined|null} seat - Seat object from the bus diagram.
 * @returns {string} Human-readable fare type for Mixpanel, or "General" when missing.
 */
const getSeatTappedFareType = (seat) => {
  const trimmedRef = String(seat?.seatFareReference ?? '').trim();
  const trimmedDesc = String(seat?.seatFareDescription ?? '').trim();
  const fareType = trimmedRef || trimmedDesc || 'General';
  return fareType.charAt(0).toUpperCase() + fareType.slice(1).toLowerCase();
};

/**
 * This component is used to render the seat selection page
 */
export class SeatSelector extends Component {
  constructor(props) {
    super(props);

    const { features } = this.props;

    const defaultDeck = features.DEFAULT_DECK === 'first' ? 0 : 1;
    this.changeSeat = this.changeSeat.bind(this);
    this.finishSeatSelection = this.finishSeatSelection.bind(this);
    this.acceptWomanPolicies = this.acceptWomanPolicies.bind(this);
    this.handlePetDialogPress = this.handlePetDialogPress.bind(this);
    this.handlePetDialogShowPress = this.handlePetDialogShowPress.bind(this);
    this.seatProps = this.seatProps.bind(this);
    this.state = {
      womanPoliciesAccepted: false,
      hidePetDialog: true,
      showAdjacentCard: false,
      toggleAdjacentCard: false,
      adjacentModalType: null, // 'warning' | 'disclaimer' | null
      hasInitializedToggle: false, // Track if we've already initialized the toggle
      errors: {
        womanPoliciesAccepted: false,
      },
      selectedDeck: defaultDeck,
    };
    this.deselectIfExceed = this.deselectIfExceed.bind(this);
    this.controlAdjacentCard = this.controlAdjacentCard.bind(this);
    this.handleAdjacentToggle = this.handleAdjacentToggle.bind(this);
    this.processAdjacentToggle = this.processAdjacentToggle.bind(this);

    this.updateAdjacentSeats = this.updateAdjacentSeats.bind(this);
    this.womanSeatsSelected = this.womanSeatsSelected.bind(this);
    this.switchDeck = this.switchDeck.bind(this);
    this.toggleSwitchDeck = this.toggleSwitchDeck.bind(this);
    this.seatTapIndex = 0;
  }

  componentDidMount() {
    const storagePetDialogHidden = localStorage.getItem('hidePetDialog');
    this.setState({ hidePetDialog: storagePetDialogHidden });
    this.deselectIfExceed();
    // Initial validation to show the adjacent seat card
    const { selectedSeats } = this.props;

    this.controlAdjacentCard(selectedSeats);
  }

  componentDidUpdate(prevProps, prevState) {
    const { showAdjacentCard, toggleAdjacentCard } = this.state;
    const { trip, way, hasSeats, isMultiTrip, selectedSeats, resultsLayout, tripPricing, seats } =
      this.props;

    if (!isMultiTrip && trip && hasSeats && !this.viewSeatsSent) {
      this.viewSeatsSent = true;
      trackViewSeats(trip, way, resultsLayout, { pricing: tripPricing, seats });
    }
    if (prevState.showAdjacentCard !== showAdjacentCard) {
      if (!showAdjacentCard && toggleAdjacentCard) {
        this.deselectIfExceed(true);
      }
    }

    if (prevState.toggleAdjacentCard !== toggleAdjacentCard) {
      if (!toggleAdjacentCard) {
        this.deselectIfExceed(true);
      }
    }

    // Control adjacent card when selectedSeats change
    if (!isEqual(prevProps.selectedSeats, selectedSeats)) {
      this.controlAdjacentCard(selectedSeats);
    }
  }

  /**
   * Handle when user accepts to continue without adjacent seats
   */
  handleAcceptAdjacentModal = () => {
    const { onFinishedSelection } = this.props;
    this.setState({ adjacentModalType: null }, () => {
      window.scrollTo(0, 0);
      this.saveConfirmedPreference();
      onFinishedSelection();
    });
  };

  /**
   * Handle when user cancels and wants to return to seat selection
   */
  handleCancelAdjacentModal = () => {
    this.setState({ adjacentModalType: null });
  };

  handlePetDialogPress() {
    const storagePetDialogHidden = localStorage.getItem('hidePetDialog');
    if (!storagePetDialogHidden) {
      localStorage.setItem('hidePetDialog', true);
    }
    this.setState({ hidePetDialog: true });
  }

  handlePetDialogShowPress() {
    this.setState({ hidePetDialog: false });
  }

  handleAdjacentToggle(e) {
    const isChecked = e.target.checked;
    this.processAdjacentToggle(isChecked);
  }

  handleQuickPurchaseFromSeats = (passengerCount, seatNumbers) => {
    const { onQuickPurchaseFromSeats, selectedSeats } = this.props;
    if (!onQuickPurchaseFromSeats) return;

    this.saveConfirmedPreference();
    const primarySeats = selectedSeats.filter(isPrimarySeatReference);
    onQuickPurchaseFromSeats(
      passengerCount,
      seatNumbers,
      cloneSelectedSeatReferences(primarySeats),
    );
  };

  getEffectiveMinPassengers() {
    const { minPassengers, requiredSeatsCount, seatEditMode } = this.props;

    if (seatEditMode === 'quickCheckout') {
      return Number(requiredSeatsCount) || 0;
    }

    return minPassengers;
  }

  saveConfirmedPreference() {
    const {
      layout,
      selectedSeats,
      bookingPassengerCount,
      tripId,
      seatPreferenceDiagramType,
      features,
      seatEditMode,
    } = this.props;

    if (seatEditMode === 'quickCheckout') return false;
    return saveConfirmedSeatPreference({
      layout,
      selectedSeats,
      passengerCount: bookingPassengerCount,
      tripSlug: tripId,
      diagramType: seatPreferenceDiagramType,
      showUniformLayout: Boolean(features.SHOW_UNIFORM_LAYOUT),
    });
  }

  withSeatZone(seat) {
    if (!seat) return seat;
    const { features, seatPriceZones, layout, trip, showPricesByGroupConfig } = this.props;
    const resolvedSeatPriceZones = getResolvedSeatPriceZonesForSeatSelector({
      features,
      seatPriceZones,
      layout,
      trip,
      showPricesByGroupConfig,
    });
    const seatZoneNumber = getSeatZoneNumberForPassengerSeat(seat, resolvedSeatPriceZones);
    return seatZoneNumber ? { ...seat, seatZoneNumber } : seat;
  }

  trackSeatTap(seat, action) {
    const { resultsLayout, routeLabel, trip, tripPricing } = this.props;
    this.seatTapIndex += 1;

    trackSeatTapped({
      seatNumber: seat?.number,
      seatFloor: seat?.seatLevel ?? 1,
      fareType: getSeatTappedFareType(seat),
      fareAmount: Number(seat?.price ?? 0),
      tapIndex: this.seatTapIndex,
      action,
      screen: resultsLayout ? 'Schedule screen' : 'Checkout breadcrumb',
      departureRoute: getSeatSelectorDepartureRoute({ routeLabel, trip }),
      pricing: tripPricing,
      seat,
    });
  }

  /**
   * Process the adjacent toggle change after any confirmations
   * @param {boolean} isChecked - Whether the toggle is checked
   */
  processAdjacentToggle(isChecked) {
    const { selectedSeats } = this.props;

    if (!isChecked) {
      this.deselectIfExceed(true);
    }

    this.setState({ toggleAdjacentCard: isChecked }, () => {
      if (!isChecked) return;
      const parentSeat = selectedSeats.find((seat) => seat.adjacentSeats);
      if (parentSeat) {
        this.updateAdjacentSeats(parentSeat);
      }
    });
  }

  switchDeck({ selectedDeck }) {
    this.setState({
      selectedDeck,
    });
  }

  toggleSwitchDeck() {
    const { selectedDeck } = this.state;
    this.switchDeck({ selectedDeck: selectedDeck === 0 ? 1 : 0 });
  }

  updateAdjacentSeats(parentSeat) {
    const { selectedSeats, seats, updateSeat } = this.props;

    if (!parentSeat || !parentSeat.adjacentSeats) return;

    /**
     * Get the adjacent seat from the selected parent seat
     * Set the adjacent seat from seats object, this because seats object has all the properties we need
     */
    const adjacentSeatNumber = parentSeat.adjacentSeats.numbers[0];
    const adjacentSeat = Object.values(seats).find((seat) => seat.number === adjacentSeatNumber);

    /** Verify if the adjacent seat is already selected or we must select it first */
    const isAdjacentPicked = checkIfAdjacentPicked(selectedSeats, parentSeat.number);

    adjacentSeat.isPickedAsAdjacent = !isAdjacentPicked;

    const updatedParentSeat = { ...parentSeat, isAdjacentPicked: true };

    const isAdjacentCurrentlySelected = checkIfAdjacentSelected(selectedSeats, adjacentSeat.number);

    if (isAdjacentCurrentlySelected) {
      updateSeat(updatedParentSeat);
      updateSeat(adjacentSeat);
    } else {
      this.changeSeat(adjacentSeat);
      updateSeat(updatedParentSeat);
    }
  }

  /**
   * Returns true if the adjacent seat is already picked
   * @param {Number} adjacentSeatNumber
   * @returns Bool
   */

  /**
   * This function will deselect the seats until we have the same number of seats as passengers
   * This happens when we have changed departure seats and in the return trip we selected more seats than passengers
   * @param {Bool} removeAdjacent - If true, will remove the adjacent seats
   */
  deselectIfExceed(removeAdjacent = false) {
    const { passengers, selectedSeats, selectSeats, isMultiTrip } = this.props;

    /**
     * For trips with connections, this case is handled by MultiTripSeatSelector.
     * So we don't need to execute this logic
     */
    if (isMultiTrip) return;

    let newSeats = selectedSeats.slice(0);
    /*
     * Avoids overwriting the state of tickets when there are no seats selected yet
     */
    if (!newSeats.length) {
      return;
    }
    if (newSeats.length >= passengers.length && !removeAdjacent) {
      // TODO: This is a temporary fix, we need to find a better way to handle this
      const passengersCount =
        passengers.length - newSeats.filter((seat) => !seat.isPickedAsAdjacent).length;
      newSeats = newSeats.slice(passengersCount * -1);
    }

    // Remove adjacent seats when removeAdjacent is true
    if (removeAdjacent) {
      newSeats = newSeats.filter((seat) => !seat.isPickedAsAdjacent);
    }

    newSeats = newSeats.map((seat) => {
      const { seats } = this.props;
      const seatFromDiagram = getSeatFromBusDiagram({
        seats,
        seatNumber: seat.number,
        seatLevel: seat?.seatLevel,
      });
      return {
        ...seat,
        ...seatFromDiagram,
        ...(seatFromDiagram.adjacentSeats && {
          adjacentSeats: { ...seat.adjacentSeats, ...seatFromDiagram.adjacentSeats },
        }),
        ...(removeAdjacent && seat.isAdjacentPicked && { isAdjacentPicked: false }),
      };
    });
    selectSeats(newSeats);
  }

  controlAdjacentCard(seatsSelected) {
    const { isExchange, adjacentSeatPolicy, hasAdjacentSeatPassenger, seats } = this.props;
    const { hasInitializedToggle } = this.state;

    // If we are in exchange mode and adjacent seat exchange is not allowed,
    // do not show the adjacent seat card
    if (isExchange && adjacentSeatPolicy !== 'primary_and_adjacent') {
      this.setState({ showAdjacentCard: false, toggleAdjacentCard: false });
      return;
    }

    // Only evaluate adjacent capable seats during initial component mount
    let initialToggleState = false;

    if (!hasInitializedToggle) {
      // This is only needed during the first mount to determine initial toggle state
      // Check if there are seats with adjacent capability
      const hasAdjacentSeats = Object.values(seats).some((seat) => seat.adjacentSeats);
      // If we are in exchange mode, the user had adjacent seats in their original purchase,
      // and there are seats with adjacent capability, we activate the toggle by default

      initialToggleState = isExchange && hasAdjacentSeatPassenger && hasAdjacentSeats;
    }

    const { isAdjacentCardVisible, shouldTurnOnToggleValue } = calculateAdjacentCardVisibility(
      seatsSelected,
      { isExchange },
    );

    const showAdjacentCard = isAdjacentCardVisible;
    // Only activate the toggle if we are in primary_and_adjacent mode or not in exchange mode
    const shouldActivateToggle = isExchange
      ? adjacentSeatPolicy === 'primary_and_adjacent' &&
        (shouldTurnOnToggleValue || initialToggleState)
      : shouldTurnOnToggleValue;

    this.setState({
      showAdjacentCard: showAdjacentCard || shouldActivateToggle,
      toggleAdjacentCard: shouldActivateToggle,
      // Mark that we've initialized the toggle if we're setting it based on initialToggleState
      hasInitializedToggle: true,
    });
  }

  changeSeat(seat) {
    const {
      selectedSeats,
      selectSeats,
      isExchange,
      features,
      clearError,
      roundTrip,
      hasAdjacentSeatPassenger,
      seats,
    } = this.props;
    const { toggleAdjacentCard } = this.state;
    const { SEAT_SELECTION_LIMIT, ROUND_SEAT_SELECTION_LIMIT, PREVENT_OVERSELECT_ON_RETURN_TRIP } =
      features;
    const { number: seatNumber, seatLevel } = seat;
    const effectiveMinPassengers = this.getEffectiveMinPassengers();

    const isSelected = selectedSeats.some(
      ({ number, seatLevel: selectedSeatLevel }) =>
        String(number) === String(seatNumber) &&
        (!seatLevel || String(selectedSeatLevel) === String(seatLevel)),
    );
    const action = isSelected ? 'REMOVE' : 'ADD';
    const tapOutcomeAction = action === 'REMOVE' ? 'Deselected' : 'Selected';

    let newSeats = selectedSeats.slice(0);

    if (action === 'ADD' && seat.category === 'seat_handicap') {
      newSeats = evictAdaCompanions(newSeats, seat);
    }

    const lastSelectedSeat = selectedSeats[selectedSeats.length - 1];
    const isLastSeatAdjacent = lastSelectedSeat ? Boolean(lastSelectedSeat.adjacentSeats) : true;
    const currentSeatsCount = newSeats.length;

    if (action === 'REMOVE') {
      if (isExchange) {
        const shouldResetSeatsList = Boolean(seat.adjacentSeats) && isLastSeatAdjacent;

        if (shouldResetSeatsList) {
          newSeats = [];
        }
      }
      newSeats = newSeats.filter(
        ({ number, seatLevel: selectedSeatLevel }) =>
          String(number) !== String(seatNumber) ||
          (seatLevel && String(selectedSeatLevel) !== String(seatLevel)),
      );
    } else {
      if (isExchange) {
        // Calculate seatsCount based on adjacentSeatPolicy
        const { adjacentSeatPolicy } = this.props;
        const seatsCount =
          hasAdjacentSeatPassenger && adjacentSeatPolicy === 'primary_and_adjacent'
            ? effectiveMinPassengers + 1
            : effectiveMinPassengers;

        const shouldResetSeatsList = currentSeatsCount >= seatsCount && toggleAdjacentCard;

        if (shouldResetSeatsList) {
          newSeats = [];
        }

        const shouldRemoveFirstSeat = seatsCount === currentSeatsCount && newSeats.length > 0;
        if (shouldRemoveFirstSeat) {
          newSeats.shift();
        }

        const seatWithZone = this.withSeatZone(seat);
        newSeats.push(seatWithZone);
        // Add the selected seat

        // If toggle is active and the seat has adjacent seat capability,
        // find and automatically add the adjacent seat
        if (
          toggleAdjacentCard &&
          seat.adjacentSeats &&
          hasAdjacentSeatPassenger &&
          adjacentSeatPolicy === 'primary_and_adjacent' &&
          !newSeats.some((s) => s.isAdjacentPicked)
        ) {
          // Use the findAdjacentSeatPair function imported at the beginning of the file
          const adjacentSeat = findAdjacentSeatPair({ seats, seat });

          // Verify that the adjacent seat exists and is not already in the selection
          if (adjacentSeat && !newSeats.some((s) => s.number === adjacentSeat.number)) {
            // Prepare the seats to be recognized as an adjacent seat grouping
            const updatedParentSeat = prepareAdjacentSeat({
              seat: seatWithZone,
              type: 'parent',
            });
            const updatedAdjacentSeat = prepareAdjacentSeat({
              seat: this.withSeatZone(adjacentSeat),
              type: 'adjacent',
            });

            newSeats.pop();
            // Replace the main seat in newSeats
            newSeats.push(updatedParentSeat); // Add the updated parent seat
            newSeats.push(updatedAdjacentSeat); // Add the adjacent seat
          }
        }

        this.trackSeatTap(seat, tapOutcomeAction);
        selectSeats(newSeats);
        return;
      }
      const attemptToAddPrimarySeat = !seat.isPickedAsAdjacent;
      const onReturnTripWithLimit = effectiveMinPassengers > 0;

      const adjacentSeatsCount = newSeats.filter((s) => s.isPickedAsAdjacent).length;
      let seatsCount = currentSeatsCount - adjacentSeatsCount;

      // When we select a seat that can be adjacent to another,
      // if we have a marked adjacent seat, we add 1 to the seat counter
      // because we are going to deactivate it with controlAdjacentCard()
      if (seat.adjacentSeats) {
        // We also verify that the number of passengers is not already equal to the number of seats
        if (adjacentSeatsCount && !(seatsCount === effectiveMinPassengers)) {
          seatsCount += 1;
        }
      }

      // TODO: We need to find a better way to handle this shifts
      if (seatsCount === effectiveMinPassengers && attemptToAddPrimarySeat) {
        newSeats.shift();

        if (newSeats.some((s) => s.isPickedAsAdjacent || s.isAdjacentPicked)) {
          newSeats.shift();
        }
      }

      if (clearError) clearError();

      newSeats.push(this.withSeatZone(seat));

      // When PREVENT_OVERSELECT_ON_RETURN_TRIP is active on a return trip:
      // If adding the current primary seat would cause the number of selected primary seats
      // (considering adjustments for adjacent seat logic) to reach minPassengers,
      // prevent the selection. This avoids exceeding minPassengers, overriding the default
      // behavior of deselecting the oldest seat.
      if (
        PREVENT_OVERSELECT_ON_RETURN_TRIP &&
        onReturnTripWithLimit &&
        seatsCount === effectiveMinPassengers &&
        attemptToAddPrimarySeat
      ) {
        // Call controlAdjacentCard with the seats that would have been selected
        // before the prevention logic to maintain consistency
        this.controlAdjacentCard(newSeats);
        this.trackSeatTap(seat, 'Blocked');
        return;
      }
    }

    let seatLimit = SEAT_SELECTION_LIMIT;
    /**
     * If it is a round trip and we have set a limit specific for round trips,
     * we use that limit instead of the default one.
     * This is useful when we have a limit of 5 seats for a round trip and 10 seats for a one way trip.
     */
    if (roundTrip && ROUND_SEAT_SELECTION_LIMIT) {
      seatLimit = ROUND_SEAT_SELECTION_LIMIT;
    }
    // Use newSeats.length instead of currentSeatsCount since newSeats may have been modified
    if (newSeats.length > seatLimit && action === 'ADD') {
      this.trackSeatTap(seat, 'Blocked');
      return;
    }

    this.trackSeatTap(seat, tapOutcomeAction);
    selectSeats(newSeats);
  }

  womanSeatsSelected() {
    const { hasSeats, features, isOpenTicket, seats, selectedSeats } = this.props;
    return (
      hasSeats &&
      Boolean(
        features.WOMAN_SEATS_ENABLED &&
          !isOpenTicket &&
          selectedSeats.some(({ number }) => isWomanSeat(seats[number].category)),
      )
    );
  }

  finishSeatSelection() {
    const {
      selectedSeats,
      sendNotification,
      onFinishedSelection,
      allowsSeatSelection,
      way,
      isExchange,
      hasAdjacentSeatPassenger,
      features,
      seatPriceZones,
      layout,
      trip,
      showPricesByGroupConfig,
      tripPricing,
    } = this.props;
    const { womanPoliciesAccepted } = this.state;
    const womanSeatSelected = this.womanSeatsSelected();
    const effectiveMinPassengers = this.getEffectiveMinPassengers();

    if (!womanPoliciesAccepted && womanSeatSelected) {
      this.setState({ errors: { womanPoliciesError: true } });
      return;
    }

    const selectedSeatCount = selectedSeats.filter((seat) => !seat.isPickedAsAdjacent).length;
    // The exact minimum-seat predicate enforced below; computed once so the tracked value and
    // the accept/reject branch can never disagree. Flows without seat selection have no
    // applicable minimum and report true.
    const meetsMinimum =
      !allowsSeatSelection ||
      (selectedSeatCount >= 1 && selectedSeatCount >= effectiveMinPassengers);

    continueWithSeatsTracker({
      way,
      selectedSeats,
      seatPriceZones: getResolvedSeatPriceZonesForSeatSelector({
        features,
        seatPriceZones,
        layout,
        trip,
        showPricesByGroupConfig,
      }),
      meetsMinimum,
      pricing: tripPricing,
    });

    if (!allowsSeatSelection) {
      return onFinishedSelection();
    }

    if (!meetsMinimum) {
      sendNotification('you_need_to_choose_some_seats', { count: effectiveMinPassengers || 1 });
      return;
    }

    // Check if we need to show the adjacent seat modal
    const selectedAdjacentSeatsCount = selectedSeats.filter(
      (seat) => seat.isPickedAsAdjacent || seat.isAdjacentPicked || seat.adjacentSeats,
    ).length;

    const { adjacentSeatPolicy } = this.props;
    const { toggleAdjacentCard } = this.state;

    const baseConditions = Boolean(
      !toggleAdjacentCard &&
        isExchange &&
        hasAdjacentSeatPassenger &&
        adjacentSeatPolicy === 'primary_and_adjacent' &&
        selectedSeatCount >= 1,
    );

    let modalType = null;

    if (baseConditions) {
      if (selectedAdjacentSeatsCount > 1) {
        modalType = 'warning';
      } else {
        modalType = 'disclaimer';
      }
    }

    if (modalType) {
      this.setState({ adjacentModalType: modalType });
      return;
    }

    // Normal flow continues if no warning needed
    window.scrollTo(0, 0);
    this.saveConfirmedPreference();
    onFinishedSelection();
  }

  acceptWomanPolicies() {
    const { womanPoliciesAccepted, errors } = this.state;
    this.setState({
      womanPoliciesAccepted: !womanPoliciesAccepted,
      errors: {
        ...errors,
        womanPoliciesError: !!womanPoliciesAccepted,
      },
    });
  }

  seatProps(seatNumber) {
    const { seats } = this.props;
    return isWomanSeat(seats[seatNumber].category)
      ? { className: 'selected-seats-boxes__woman' }
      : {};
  }

  render() {
    const {
      way,
      layout,
      selectedSeats,
      lockedSeats,
      canGoBack,
      onBackAction,
      diagramType,
      allowsSeatSelection,
      isOpenTicket,
      t,
      seats,
      features,
      busCategories,
      adjacentSeatPolicy,
      isExchange,
      isLoading,
      isPetFriendly,
      tripPricing,
      tripId,
      resultsLayout,
      onClose,
      hasSeats,
      trip,
      fetchingDetailsError,
      copies,
      isMultiTrip,
      firstTripIsActive,
      secondTripIsActive,
      routeLabel,
      headerLegend,
      passengerTypes,
      firstFloorReclination,
      secondFloorReclination,
      roundTrip,
      hasAdjacentSeatPassenger,
      overbooking,
      onQuickPurchaseFromSeats,
      frequentPassengersCount,
      seatPriceZones,
      showPricesByGroupConfig,
      embedded,
      seatEditMode,
    } = this.props;

    const {
      womanPoliciesAccepted,
      hidePetDialog,
      showAdjacentCard,
      toggleAdjacentCard,
      selectedDeck,
      adjacentModalType,
    } = this.state;

    const effectiveMinPassengers = this.getEffectiveMinPassengers();
    const isQuickCheckoutSeatEdit = seatEditMode === 'quickCheckout';
    const bfoPricingSupported = isBfoSupportedSeatSelectionFlow({
      features,
      isExchange,
      isOpenTicket,
      hasConnections: isMultiTrip || Boolean(trip?.hasConnections),
      isQuickPurchase: isQuickCheckoutSeatEdit,
    });

    let buttonContext = overbooking
      ? t('seats:select_seats_overbooking', { count: selectedSeats.length })
      : t('seats:select_seats', { count: selectedSeats.length });
    const selectedPassengerSeats = selectedSeats.filter((seat) => !seat.isPickedAsAdjacent).length;
    if (selectedPassengerSeats < effectiveMinPassengers) {
      buttonContext = overbooking
        ? t('seats:seat_quantity_overbooking', {
            count: effectiveMinPassengers - selectedPassengerSeats,
          })
        : t('seats:seat_quantity', { count: effectiveMinPassengers - selectedPassengerSeats });
    } else if (isQuickCheckoutSeatEdit) {
      buttonContext = t('purchase:quick_checkout.save_seat_selection');
    }

    if (!allowsSeatSelection) {
      buttonContext = t('seats:next');
    } else if (selectedSeats.length === 0) {
      if (effectiveMinPassengers) {
        buttonContext = overbooking
          ? t('seats:seat_quantity_overbooking', {
              count: effectiveMinPassengers - selectedSeats.length,
            })
          : t('seats:seat_quantity', { count: effectiveMinPassengers - selectedSeats.length });
      } else {
        buttonContext = overbooking
          ? t('seats:select_at_least_one_seat_overbooking')
          : t('seats:select_at_least_one_seat');
      }
    }

    const busHasPetFriendlySeats =
      hasSeats &&
      Object.keys(seats).some((key) => {
        return isPetFriendlySeat(seats[key].category);
      });
    const tripIsPetFriendly = isPetFriendly;
    const showPetFriendly = Boolean(busHasPetFriendlySeats && tripIsPetFriendly);

    const womanSeatSelected = this.womanSeatsSelected();

    const showWomanLegends = Boolean(
      hasSeats &&
        Boolean(
          features.WOMAN_SEATS_ENABLED &&
            !isOpenTicket &&
            Boolean(seats.categories?.find((category) => isWomanSeat(category))),
        ),
    );

    const petFriendlyAvailability =
      hasSeats &&
      Boolean(
        Object.keys(seats).some(
          (key) => isPetFriendlySeat(seats[key].category) && !isSeatOccupied(seats[key]),
        ),
      ) &&
      Boolean(busCategories?.some(({ type }) => isPetFriendlySeat(type)));

    const petFriendlySoldOut =
      isPetFriendly && !petFriendlyAvailability && features.SOLDOUT_PET_MESSAGE_ENABLED;
    // TODO Verify if this is the correct way to check if the trip is pet friendly when backend is ready
    const petFriendlyOnTheTrip =
      hasSeats &&
      petFriendlyAvailability &&
      Boolean(
        Object.keys(seats).some(
          (key) => isSeatOccupied(seats[key]) && seats[key].category === 'pet_friendly',
        ),
      );

    const isSelectedPetFriendly = selectedSeats.some(({ category }, _, array) => {
      return showPetFriendly && isPetFriendlySeat(category) && array.length > 1;
    });

    const isMainButtonDisabled =
      (features.WOMAN_SEATS_ENABLED && womanSeatSelected && !womanPoliciesAccepted) || isLoading;

    const { errors } = this.state;
    const { womanPoliciesError } = errors;
    const isFlat = features.FUNNEL_STYLE === 'FLAT';
    const isMobile = !isDesktopSize();

    const isSeatsOnResults = isSeatsOnResultActivated();

    const showAdjacentIcon =
      (isExchange && adjacentSeatPolicy !== 'disabled' && hasAdjacentSeatPassenger) || !isExchange;
    const resolvedSeatPriceZones = getResolvedSeatPriceZonesForSeatSelector({
      features,
      seatPriceZones,
      layout,
      trip,
      showPricesByGroupConfig,
    });
    const shouldApplySeatZones = resolvedSeatPriceZones.length > 0;

    const busDiagramComponent = hasSeats ? (
      <ConnectedBusDiagram
        id={tripId}
        layout={layout}
        diagramType={diagramType}
        disabled={!allowsSeatSelection}
        selectedSeats={selectedSeats}
        lockedSeats={lockedSeats}
        changeSeat={this.changeSeat}
        seats={selectedSeats}
        seatProps={this.seatProps}
        showPetFriendly={isPetFriendly}
        petFriendlySoldOut={petFriendlySoldOut}
        showAdjacentIcon={showAdjacentIcon}
        isResultsLayout={resultsLayout}
        selectedDeck={selectedDeck}
        switchDeck={this.switchDeck}
        key={tripId}
        verticalSeats={features.VERTICAL_SEATS_LAYOUT}
        firstFloorReclination={firstFloorReclination}
        secondFloorReclination={secondFloorReclination}
        showUniformLayout={features.SHOW_UNIFORM_LAYOUT}
        adjacentToggleActive={toggleAdjacentCard}
        seatPriceZones={resolvedSeatPriceZones}
      />
    ) : (
      <Loading loadingText={t('seats:searching_available_seats')} />
    );

    const hasDeckSelection = layout?.[1];
    const hasSecondFloor = trip?.secondFloor;

    const seatsCount = getSeatsCount({ seats, selectedSeats });
    /** Resolve floor label from trip data. */
    const getTripFloor = (trip) => {
      if (trip?.floor) {
        return trip.floor;
      }
      return '1';
    };

    /** Render seats legend based on layout variant and mobile mode. */
    const seatsLegend = (isBoxMobile = isFlat) =>
      isSeatsOnResults ? (
        <NewSeatsLegend
          layout={layout}
          seatPriceZones={resolvedSeatPriceZones}
          useSeatZoneLegend={shouldApplySeatZones}
          showPetFriendly={showPetFriendly}
          showWomanLegends={showWomanLegends}
          occupied={seatsCount.occupied}
          available={seatsCount.available}
          selected={seatsCount.selected}
          isHorizontal={!resultsLayout || isFlat}
          isBoxMobile={isBoxMobile}
          showAdjacentSeatLegend={showAdjacentIcon}
          adjacentToggleActive={toggleAdjacentCard}
          floor={getTripFloor(trip)}
          hasSecondFloor={hasSecondFloor}
        />
      ) : (
        <SeatsLegend
          layout={layout}
          showPetFriendly={showPetFriendly}
          showWomanLegends={showWomanLegends}
          occupied={seatsCount.occupied}
          available={seatsCount.available}
          selected={seatsCount.selected}
          showAdjacentSeatLegend={showAdjacentIcon}
          adjacentToggleActive={toggleAdjacentCard}
        />
      );

    const pricingComponent = (
      <SeatsPreviewPricing
        id={tripId}
        bfoPricingSupported={bfoPricingSupported}
        tripPricing={tripPricing}
        seatPriceZones={resolvedSeatPriceZones}
        passengerTypes={passengerTypes}
        seatsSelected={selectedSeats}
        ctaText={buttonContext}
        showWomanSwitch={womanSeatSelected}
        onWomanSwitchChange={this.acceptWomanPolicies}
        womanSwitchValue={womanPoliciesAccepted}
        womanPoliciesError={womanPoliciesError}
        showPetFriendly={isPetFriendly}
        onContinue={this.finishSeatSelection}
        onSelectAdjacent={this.handleAdjacentToggle}
        showAdjacentCard={showAdjacentCard}
        adjacentSwitchValue={toggleAdjacentCard}
        overbooking={overbooking}
        onQuickPurchaseFromSeats={
          onQuickPurchaseFromSeats ? this.handleQuickPurchaseFromSeats : undefined
        }
        frequentPassengersCount={frequentPassengersCount}
        compact={isQuickCheckoutSeatEdit}
      />
    );

    const overbookingComponent = (
      <div
        className={`bg-gray-100 flex items-center justify-center w-full h-52 rounded-xl border border-gray-200 ${
          resultsLayout ? 'md:rounded-l-[60px] md:rounded-r-[20px]' : ''
        } ${isFlat ? 'md:rounded-l-[60px] md:rounded-r-[20px]' : ''} ${
          isMobile ? 'rounded-t-[60px] rounded-b-[20px]' : ''
        }`}
      >
        <OverbookingPassengerSelector trip={trip} way={way} selectedSeats={selectedSeats} />
      </div>
    );

    const diagramContent =
      overbooking && resultsLayout ? overbookingComponent : busDiagramComponent;

    if (isLoading && !resultsLayout) return <Loading loadingText={t('seats:loading')} />;

    const hasSelectedSeats = Boolean(selectedSeats.length);
    const modalTitle =
      way === 'departure'
        ? t('purchase:title.choose_your_departing_seats')
        : t('purchase:title.choose_your_return_seats');

    const notMultipleDecksComponent = isFlat ? (
      <Text weight="bold" mobileSize="M">
        {i18n.t('seats:single_deck', { context: diagramType })}
      </Text>
    ) : null;

    const floorsLabel = trip.floor === '2' ? t('seats:2nd_floor') : t('seats:1st_floor');

    const headerTitle = hasSecondFloor
      ? `${t('seats:your_seats')} - ${floorsLabel}`
      : t('seats:your_seats');

    const FloorLabelComponent = isFlat ? (
      <Text weight="bold" fontFamily="secondary">
        {headerTitle}
      </Text>
    ) : null;

    const lang = CurrentLang({ shouldReadDefaultLanguageFromStore: true });
    const { seatsDisclaimer, seatDisclaimerVan } = copies.policies[lang] || {};
    const seatsDisclaimerText = diagramType === 'van' ? seatDisclaimerVan : seatsDisclaimer;

    const overbookingToggleComponent = (
      <div className="flex justify-start mb-2">
        <Badge type="gray" roundCorners smallPadding={false} hasShadow={false}>
          <div className="flex items-center px-2 py-1">
            <Text color="primary" weight="semibold" size="M" mobileSize="S" elementType="span">
              {t('general:text.no_assigned_seat')}
            </Text>
          </div>
        </Badge>
      </div>
    );

    /** Render deck/overbooking header controls for seats view. */
    const toggleHeader = (fullWidth, mobileFullWidth) => {
      let toggleContent;

      if (overbooking) {
        toggleContent = overbookingToggleComponent;
      } else if (hasDeckSelection) {
        toggleContent = (
          <Toggle
            isChecked={selectedDeck === 1}
            onClick={this.toggleSwitchDeck}
            isMobile={isMobile}
            isFlat={isFlat}
            firstLabel={`${i18n.t('seats:1st_floor')}${
              firstFloorReclination ? ` - ${firstFloorReclination}` : ''
            }`}
            secondLabel={`${i18n.t('seats:2nd_floor')}${
              secondFloorReclination ? ` - ${secondFloorReclination}` : ''
            }`}
            firstIcon={showPetFriendly && PetBadge}
            fullWidth={fullWidth}
            mobileFullWidth={mobileFullWidth}
            id={`deck-toggle-${tripId}`}
          />
        );
      } else if (hasSecondFloor) {
        toggleContent = FloorLabelComponent;
      } else {
        toggleContent = notMultipleDecksComponent;
      }

      return (
        <Spacing size="XXS" mobileAlign="center" vertical>
          {toggleContent}
          {Boolean(seatsDisclaimerText) && <Text size="XS">{seatsDisclaimerText}</Text>}
        </Spacing>
      );
    };

    const isFlatStyle = features.FUNNEL_STYLE === 'FLAT';
    const hasAdjacentSeats = Object.values(seats).some((seat) => seat && seat?.adjacentSeats);
    const seatsLayoutVariant = isQuickCheckoutSeatEdit ? 'quickCheckout' : undefined;

    // No longer needed as we're using modals instead of disclaimer
    // const selectedSeatsAdjacent = selectedSeats.filter(
    //   (seat) => seat.isPickedAsAdjacent || seat.isAdjacentPicked,
    // );

    // No longer needed as we're using modals instead of disclaimer
    // const shouldShowDisclaimer = selectedSeatsAdjacent.length !== 2 && hasAdjacentSeatPassenger;

    return (
      <>
        {hasAdjacentSeats && <AdjacentSeatOnboarding />}

        <When condition={resultsLayout}>
          {/* In embedded mode we skip the nested ModalWithTheme so the selector swaps in place
              of the host view (e.g. inside the Quick Checkout modal) instead of stacking modals. */}
          <When condition={isMobile && !embedded}>
            <ModalWithTheme
              footer={!fetchingDetailsError && pricingComponent}
              removeContentPadding
              title={!fetchingDetailsError && modalTitle}
              hasBlushing
              expandedModal
              onCloseModal={onClose}
            >
              {!fetchingDetailsError && (
                <NewSeatsLayout
                  pricing={<div className="seats-preview-pricing">{pricingComponent}</div>}
                  toggle={toggleHeader(true)}
                  seatsLegend={seatsLegend(isFlatStyle)}
                  diagramContent={diagramContent}
                  isLoading={isLoading}
                  mobileHeader={<MobileDetailResult trip={trip} />}
                  isFlat={isFlat}
                  hasOverbooking={overbooking}
                  variant={seatsLayoutVariant}
                />
              )}
              {fetchingDetailsError && <BusDiagramNotFoundError isFlat={isFlatStyle} />}
            </ModalWithTheme>
          </When>
          <When condition={!isMobile || embedded}>
            {!fetchingDetailsError && (
              <NewSeatsLayout
                pricing={pricingComponent}
                toggle={toggleHeader(true)}
                seatsLegend={seatsLegend(isFlatStyle)}
                diagramContent={diagramContent}
                isLoading={isLoading}
                isFlat={isFlat}
                hasOverbooking={overbooking}
                variant={seatsLayoutVariant}
              />
            )}

            {fetchingDetailsError && <BusDiagramNotFoundError isFlat={isFlatStyle} />}
          </When>
          {showPetFriendly && (
            <PetsDialog hideDialog={hidePetDialog} onPress={this.handlePetDialogPress} />
          )}
        </When>

        {/* Layout for purchase flow */}
        <When condition={!resultsLayout}>
          <div className="seats-container">
            <Spacing vertical size="M">
              <When condition={allowsSeatSelection}>
                <DotersSessionAlert />
                <div className="seats-summary">
                  <HeaderDiagramBus
                    handlePetDialogShowPress={this.handlePetDialogShowPress}
                    showPetFriendly={isPetFriendly}
                    legend={headerLegend}
                    floor={getTripFloor(trip)}
                    hasSecondFloor={trip?.secondFloor}
                  />
                  {seatsLegend(false)}
                </div>
                {isSeatsOnResults && toggleHeader(undefined, true)}
                {busDiagramComponent}
                <AdjacentSeatCard
                  isVisible={showAdjacentCard}
                  hasToggleSwitch
                  title={t('seats:adjacent_seat.title')}
                  text={t('seats:adjacent_seat.message')}
                  onChange={this.handleAdjacentToggle}
                  checked={toggleAdjacentCard}
                />
              </When>
              <When condition={!allowsSeatSelection}>
                <NotAllowedSeats way={way} t={t} isOpenTicket={isOpenTicket} />
              </When>

              {features.WOMAN_SEATS_ENABLED && womanSeatSelected && (
                <div className="seats-action-disclaimer">
                  <Checkbox
                    name="woman-disclaimer"
                    id="woman-disclaimer"
                    label={t('seats:confirm_iam_woman')}
                    checked={womanPoliciesAccepted}
                    onChange={this.acceptWomanPolicies}
                  />
                </div>
              )}

              <CheckoutNavigation
                leftContent={
                  <Spacing vertical size="S">
                    {hasSelectedSeats && (
                      <MobileBottomContainer>
                        <AdjacentSeatCard
                          isVisible={showAdjacentCard}
                          hasToggleSwitch
                          title={t('seats:adjacent_seat.title')}
                          onChange={this.handleAdjacentToggle}
                          checked={toggleAdjacentCard}
                          isMobile
                        />

                        <CounterSeat
                          roundTrip={roundTrip}
                          hasPetFriendly={isSelectedPetFriendly}
                          hasScale={isMultiTrip}
                          firstTripIsActive={firstTripIsActive}
                          secondTripIsActive={secondTripIsActive}
                          routeLabel={routeLabel}
                        >
                          {hasSeats && (
                            <SelectedSeats
                              seats={selectedSeats}
                              applySeatZones={shouldApplySeatZones}
                              seatPriceZones={resolvedSeatPriceZones}
                            />
                          )}
                        </CounterSeat>
                      </MobileBottomContainer>
                    )}
                    {canGoBack && (
                      <p className="seats-action-link" onClick={onBackAction}>
                        {t('seats:change_departing_trip_seats')}
                      </p>
                    )}
                  </Spacing>
                }
              >
                {petFriendlyOnTheTrip && (
                  <Box
                    paddingHorizontal="XS"
                    paddingVertical="XS"
                    paddingHorizontalMobile="S"
                    paddingVerticalMobile="S"
                    borderRadius="S"
                    hasShadow
                  >
                    <InfoText>{t('seats:pet_dialog.disclaimer_pet_on_board')}</InfoText>
                  </Box>
                )}
                <MainButton
                  label={buttonContext}
                  onClick={this.finishSeatSelection}
                  isDisabled={isMainButtonDisabled}
                  isLoading={isLoading}
                />
                {showPetFriendly && (
                  <PetsDialog hideDialog={hidePetDialog} onPress={this.handlePetDialogPress} />
                )}
              </CheckoutNavigation>
            </Spacing>
          </div>
        </When>
        <AdjacentSeatWarningDialog
          show={adjacentModalType === 'warning'}
          onAccept={this.handleAcceptAdjacentModal}
          onCancel={this.handleCancelAdjacentModal}
        />
        <AdjacentSeatDisclaimerDialog
          show={adjacentModalType === 'disclaimer'}
          onAccept={this.handleAcceptAdjacentModal}
          onCancel={this.handleCancelAdjacentModal}
        />
      </>
    );
  }
}

SeatSelector.propTypes = propTypes;

SeatSelector.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
  copies: state.whitelabelConfig.copies,
  showPricesByGroupConfig: state.whitelabelConfig?.env?.showPricesByGroupConfig,
});

export default connect(mapStateToProps)(withTranslation('')(SeatSelector));
